import { createSlug } from './slug';
import { getMediaUrl } from './mediaUrl';

export function getSpecialties(getContent) {
  const list = getContent('formations.specialties');
  if (Array.isArray(list) && list.length > 0) return list;
  return [];
}

export function getAllCourses(getContent) {
  const grid = getContent('formations.formationsGrid') || {};
  return (grid.formations || []).map((f) => ({
    ...f,
    slug: f.slug || createSlug(f.title),
  }));
}

/** Courses linked via course.specialtyId and/or specialty.courseIds (admin may save either). */
export function resolveCoursesForSpecialty(getContent, specialty) {
  if (!specialty?.id) return [];
  const courses = getAllCourses(getContent);
  const bySpecialtyId = courses.filter((c) => c.specialtyId === specialty.id);
  const courseIds = specialty.courseIds || [];
  if (!courseIds.length) return bySpecialtyId;

  const byCourseIds = courses.filter((c) => courseIds.includes(c.id));
  const seen = new Set();
  return [...bySpecialtyId, ...byCourseIds].filter((course) => {
    if (seen.has(course.id)) return false;
    seen.add(course.id);
    return true;
  });
}

export function getCoursesForSpecialty(getContent, specialtyId) {
  const specialty = getSpecialties(getContent).find((s) => s.id === specialtyId);
  return resolveCoursesForSpecialty(getContent, specialty);
}

export function findSpecialty(getContent, specialtySlug) {
  return getSpecialties(getContent).find(
    (s) => s.slug === specialtySlug || s.id === specialtySlug
  );
}

export function findCourse(getContent, specialtySlug, courseSlug) {
  const specialty = findSpecialty(getContent, specialtySlug);
  if (!specialty) return null;
  const courses = getCoursesForSpecialty(getContent, specialty.id);
  return courses.find((c) => c.slug === courseSlug || c.id === courseSlug) || null;
}

export function getCoursePath(specialtySlug, courseSlug) {
  return `/formations/${specialtySlug}/${courseSlug}`;
}

export function getSpecialtyPath(specialtySlug) {
  return `/formations/${specialtySlug}`;
}

/** Legacy flat URL — resolve to nested path when possible */
export function resolveLegacyCoursePath(getContent, flatSlug) {
  const courses = getAllCourses(getContent);
  const course = courses.find((c) => c.slug === flatSlug || c.id === flatSlug);
  if (course?.specialtyId) {
    const specialty = getSpecialties(getContent).find((s) => s.id === course.specialtyId);
    if (specialty) return getCoursePath(specialty.slug || specialty.id, course.slug);
  }
  const specialty = findSpecialty(getContent, flatSlug);
  if (specialty) return getSpecialtyPath(specialty.slug || specialty.id);
  return null;
}

/** Grouped options for formation select fields (inscription + testimonials) */
export function getFormationSelectOptions(getContent) {
  const specialties = getSpecialties(getContent);

  return specialties
    .map((specialty) => ({
      specialtyId: specialty.id,
      label: specialty.title,
      courses: resolveCoursesForSpecialty(getContent, specialty),
    }))
    .filter((group) => group.courses.length > 0);
}

/** Guess catalog course id from featured card title when CMS courseId is missing */
export function guessCourseIdFromTitle(title, programId = '') {
  const t = (title || '').toLowerCase();
  if (/cone beam|cbct|cone-beam/.test(t)) return 'cone-beam';
  if (/mini vis|mini-vis/.test(t)) return 'orthodontie-minivis';
  if (/prothèse sur implant|prothese implantaire|prothèse implantaire|prothese sur implant/.test(t)) return 'prothese-implantaire';
  if (/masterclass.*prothèse|prothèse fix|prothese fix|bridge/.test(t)) return 'prothese-fixee';
  if (/implantolog/.test(t)) return 'implantologie-cycle-avance';
  if (/esthétique|esthetique|facette/.test(t) && !/sourire gingival|orthodont/.test(t)) return 'dentisterie-esthetique';
  if (/orthodont|classe ii|classe iii|sourire gingival|malocclusion/.test(t)) return 'orthodontie-minivis';
  if (/sagesse|revodent|chirurgie orale|extraction/.test(t)) return 'chirurgie-orale';

  const idMap = {
    'cone-beam': 'cone-beam',
    chirurgie: 'chirurgie-orale',
    prothese: 'prothese-fixee',
    implantologie: 'implantologie-cycle-avance',
    esthetique: 'dentisterie-esthetique',
    orthodontie: 'orthodontie-minivis',
  };
  if (programId && idMap[programId]) return idMap[programId];
  return '';
}

function resolveCourseIdLink(getContent, courseId) {
  if (!courseId) return null;
  const courses = getAllCourses(getContent);
  const course = courses.find((c) => c.id === courseId || c.slug === courseId);
  if (!course?.specialtyId) return null;
  const specialty = getSpecialties(getContent).find((s) => s.id === course.specialtyId);
  if (!specialty) return null;
  return getCoursePath(specialty.slug || specialty.id, course.slug || course.id);
}

/** Resolve homepage featured program link to nested course URL when possible */
export function resolveFeaturedProgramLink(program, getContent) {
  const courseId =
    program?.courseId || guessCourseIdFromTitle(program?.title, program?.id);
  const courseLink = resolveCourseIdLink(getContent, courseId);
  if (courseLink) return courseLink;

  const link = program?.link || '';
  if (!link) return '/formations';

  const hashMatch = link.match(/#([^/?#]+)/);
  if (hashMatch) {
    const resolved = resolveLegacyCoursePath(getContent, decodeURIComponent(hashMatch[1]));
    if (resolved) return resolved;
  }

  const parts = link.replace(/\/$/, '').split('/').filter(Boolean);
  if (parts[0] === 'formations' && parts.length === 2) {
    const resolved = resolveLegacyCoursePath(getContent, parts[1]);
    if (resolved && resolved !== getSpecialtyPath(parts[1])) return resolved;
    if (findSpecialty(getContent, parts[1])) return getSpecialtyPath(parts[1]);
  }

  if (parts[0] === 'formations' && parts.length >= 3) return link;

  return link;
}

/** Homepage featured card image: CMS upload only (no catalogue guess — posters are course-specific). */
export function getFeaturedProgramImage(program) {
  return getMediaUrl(program?.image) || '';
}
